import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { MultiSelect } from "@/components/ui/multi-select"
import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"
import { Zap, Pencil, Clock, Check, X, FileText, Calculator, Plus, Trash2, PanelRightClose } from "lucide-react"
import { isFieldVisible, evalFormula } from "@/lib/agents/formlogic"
import {
  useFormRecords,
  loadFieldHistory,
  type FormWithRecord,
  type FormRecordEvent,
} from "../hooks/useFormRecords"

interface FormPanelProps {
  contactId: string | null | undefined
  teamId?: string | null
  actorId?: string
  onCollapse?: () => void
  refreshSignal?: unknown
  pollMs?: number
  // Omit in moderation to show all team-agent forms; set to scope to one agent.
  agentId?: number | string | null
  // True while the handling agent is still being determined for a freshly
  // selected conversation. Distinguishes "agent unknown" from "no agent" so we
  // don't briefly render the all-team-agents fallback with the wrong forms.
  resolving?: boolean
  variant?: "panel" | "embedded"
}

export function FormPanel({ contactId, teamId, actorId, onCollapse, refreshSignal, pollMs, agentId, resolving = false, variant = "panel" }: FormPanelProps) {
  const { forms: loadedForms, loading: recordsLoading, editField, refresh } = useFormRecords(contactId, teamId, pollMs, agentId)
  // While resolving, the agent is unknown; hold empty forms + a loading state
  // rather than showing the broad fallback for the transient null agent.
  const forms = resolving ? [] : loadedForms
  const loading = resolving || recordsLoading

  const firstRun = useRef(true)
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return }
    refresh()
  }, [refreshSignal, refresh])

  if (variant === "embedded") {
    return (
      <div className="flex flex-col h-full min-h-0 bg-muted/10">
        <div className="shrink-0 flex items-center gap-1.5 px-3 py-2 border-b text-[10px] font-semibold text-muted-foreground">
          <FileText className="h-3 w-3" />
          Forms
        </div>
        <div className="flex-1 min-h-0 overflow-auto px-2.5 py-2 space-y-3">
          {loading && forms.length === 0 && <p className="text-[10px] text-muted-foreground px-1">Loading…</p>}
          {!loading && forms.length === 0 && (
            <p className="text-[10px] text-muted-foreground px-1">No forms bound to this agent.</p>
          )}
          {forms.map((fwr) => (
            <FormCard key={fwr.form.id} fwr={fwr} onEdit={editField} actorId={actorId} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full border rounded-lg overflow-hidden bg-background">
      <div className="px-3 py-2 border-b flex items-center gap-2 text-sm font-semibold">
        <FileText className="h-4 w-4 text-muted-foreground" />
        Forms
        {onCollapse && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 ml-auto -mr-1 text-muted-foreground"
            onClick={onCollapse}
            title="Hide forms"
          >
            <PanelRightClose className="h-4 w-4" />
          </Button>
        )}
      </div>
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-3 space-y-4">
          {loading && <p className="text-xs text-muted-foreground text-center py-4">Loading…</p>}
          {!loading && forms.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-6">
              No forms bound to this conversation's agent.
            </p>
          )}
          {forms.map((fwr) => (
            <FormCard key={fwr.form.id} fwr={fwr} onEdit={editField} actorId={actorId} />
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}

function FormCard({
  fwr,
  onEdit,
  actorId,
}: {
  fwr: FormWithRecord
  onEdit: (formId: string, field: string, value: any, opts?: { actor?: string; note?: string; lock?: boolean }) => Promise<void>
  actorId?: string
}) {
  const { form, record } = fwr
  const values = record?.values || {}
  const visibleFields = form.fields.filter((f) => isFieldVisible(f, values))
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-semibold">{form.title}</span>
        {record && (
          <span className="ml-auto text-[10px] rounded-full bg-emerald-100 text-emerald-700 px-1.5 py-0.5 font-medium">
            v{record.version}
          </span>
        )}
      </div>
      <div className="space-y-2">
        {visibleFields.map((field) =>
          field.type === "group" ? (
            <GroupFieldRow
              key={field.name}
              formId={form.id}
              field={field}
              value={Array.isArray(record?.values?.[field.name]) ? record!.values[field.name] : []}
              onEdit={onEdit}
              actorId={actorId}
            />
          ) : (
            <FieldRow
              key={field.name}
              formId={form.id}
              recordId={record?.id || null}
              field={field}
              // Calculated fields derive live so they show before the filler persists them.
              value={field.calculated?.formula ? evalFormula(field.calculated.formula, values) : (record?.values?.[field.name] ?? null)}
              meta={record?.field_meta?.[field.name]}
              allValues={values}
              onEdit={onEdit}
              actorId={actorId}
            />
          )
        )}
      </div>
    </div>
  )
}

function FieldRow({
  formId,
  recordId,
  field,
  value,
  meta,
  allValues,
  onEdit,
  actorId,
}: {
  formId: string
  recordId: string | null
  field: FormFieldDef
  value: any
  meta?: { source?: string; confidence?: number | null }
  allValues?: Record<string, any>
  onEdit: (formId: string, field: string, value: any, opts?: { actor?: string; note?: string; lock?: boolean }) => Promise<void>
  actorId?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<any>(value)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  const isCalculated = !!field.calculated?.formula
  const isEmpty = isValueEmpty(value)
  const isRequiredMissing = field.required && isEmpty && !isCalculated
  const source = meta?.source
  const confidence = meta?.confidence

  async function save() {
    setSaving(true)
    try {
      await onEdit(formId, field.name, draft, { actor: actorId, lock: field.extraction?.lockAfterHuman })
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className={cn(
        "rounded-lg border p-2.5 bg-background",
        source === "human" && "border-blue-300 ring-2 ring-blue-100",
        isRequiredMissing && "border-amber-300"
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
          {field.title || field.name}
          {field.required && <span className="text-red-500">*</span>}
        </span>
        <div className="flex items-center gap-1.5">
          {isCalculated && (
            <span className="text-[10px] rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5 font-medium flex items-center gap-0.5">
              <Calculator className="h-2.5 w-2.5" />
              Calculated
            </span>
          )}
          {!isCalculated && source === "agent" && (
            <span className="text-[10px] rounded-full bg-violet-100 text-violet-700 px-1.5 py-0.5 font-medium flex items-center gap-0.5">
              <Zap className="h-2.5 w-2.5" />
              {confidence != null ? confidence.toFixed(2) : "AI"}
            </span>
          )}
          {source === "human" && (
            <span className="text-[10px] rounded-full bg-blue-100 text-blue-700 px-1.5 py-0.5 font-medium flex items-center gap-0.5">
              <Pencil className="h-2.5 w-2.5" />
              Edited
            </span>
          )}
          {isRequiredMissing && (
            <span className="text-[10px] rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 font-medium">
              required
            </span>
          )}
          {recordId && (
            <FieldHistory recordId={recordId} field={field.name} label={field.title || field.name} />
          )}
        </div>
      </div>

      {isCalculated ? (
        <div
          className={cn("mt-1 text-sm font-medium cursor-help", isEmpty && "text-muted-foreground font-normal italic")}
          title={calcTooltip(field, allValues || {}, value)}
        >
          {isEmpty ? "—" : formatValue(field, value)}
        </div>
      ) : !editing ? (
        <div
          className={cn("mt-1 text-sm font-medium cursor-pointer", isEmpty && "text-muted-foreground font-normal italic")}
          onClick={() => setEditing(true)}
          title="Click to edit"
        >
          {formatValue(field, value)}
        </div>
      ) : (
        <div className="mt-1.5 space-y-2">
          <FieldEditor field={field} value={draft} onChange={setDraft} />
          <div className="flex items-center gap-1.5">
            <Button size="sm" className="h-6 px-2 text-[11px]" onClick={save} disabled={saving}>
              <Check className="h-3 w-3 mr-0.5" />Save
            </Button>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => setEditing(false)} disabled={saving}>
              <X className="h-3 w-3 mr-0.5" />Cancel
            </Button>
          </div>
        </div>
      )}

      {source === "agent" && confidence != null && !editing && (
        <Progress
          value={Math.round(confidence * 100)}
          className="mt-1.5 h-1 bg-muted [&>div]:bg-violet-400"
        />
      )}
    </div>
  )
}

function GroupFieldRow({
  formId,
  field,
  value,
  onEdit,
  actorId,
}: {
  formId: string
  field: FormFieldDef
  value: any[]
  onEdit: (formId: string, field: string, value: any, opts?: { actor?: string; note?: string; lock?: boolean }) => Promise<void>
  actorId?: string
}) {
  const [saving, setSaving] = useState(false)
  const subfields = field.subfields || []
  const entries: Record<string, any>[] = Array.isArray(value) ? value : []

  const commit = async (next: Record<string, any>[]) => {
    setSaving(true)
    try {
      await onEdit(formId, field.name, next, { actor: actorId })
    } finally {
      setSaving(false)
    }
  }

  const addEntry = () => commit([...entries, {}])
  const removeEntry = (i: number) => commit(entries.filter((_, idx) => idx !== i))
  const setSub = (i: number, subName: string, v: any) =>
    commit(entries.map((e, idx) => (idx === i ? { ...e, [subName]: v } : e)))

  return (
    <div className="rounded-lg border p-2.5 bg-background">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
          {field.title || field.name}
          {field.required && <span className="text-red-500">*</span>}
          <span className="text-[9px] rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5">repeating</span>
        </span>
        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={addEntry} disabled={saving}>
          <Plus className="h-3 w-3 mr-0.5" />Add
        </Button>
      </div>

      {entries.length === 0 && (
        <p className="mt-1 text-xs text-muted-foreground italic">No entries yet.</p>
      )}

      <div className="mt-2 space-y-2">
        {entries.map((entry, i) => (
          <div key={i} className="rounded-md border bg-muted/20 p-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-medium text-muted-foreground">#{i + 1}</span>
              <button className="text-muted-foreground hover:text-destructive" onClick={() => removeEntry(i)} disabled={saving} title="Remove entry">
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
            <div className="space-y-1.5">
              {subfields.map((sf) => (
                <div key={sf.name}>
                  <div className="text-[10px] text-muted-foreground mb-0.5">
                    {sf.title || sf.name}{sf.required && <span className="text-red-500"> *</span>}
                  </div>
                  <FieldEditor field={sf} value={entry[sf.name] ?? null} onChange={(v) => setSub(i, sf.name, v)} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function FieldEditor({ field, value, onChange }: { field: FormFieldDef; value: any; onChange: (v: any) => void }) {
  if (field.type === "boolean") {
    return (
      <div className="flex items-center gap-2">
        <Switch checked={!!value} onCheckedChange={onChange} className="scale-75" />
        <span className="text-xs text-muted-foreground">{value ? "true" : "false"}</span>
      </div>
    )
  }
  if (field.type === "number") {
    return (
      <Input
        type="number"
        className="h-8 text-sm"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      />
    )
  }
  if (field.type === "date") {
    return (
      <Input
        type="date"
        className="h-8 text-sm"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
      />
    )
  }
  if (field.type === "multiselect" && field.list?.length) {
    return (
      <MultiSelect
        options={field.list.map((o) => ({ label: o.label, value: o.value }))}
        value={Array.isArray(value) ? value : value != null ? [value] : []}
        onValueChange={onChange}
        placeholder="Select…"
        className="h-8"
        maxCount={3}
      />
    )
  }
  if (field.type === "list" && field.list?.length) {
    return (
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-sm">
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          {field.list.map((o) => (
            <SelectItem key={o.value} value={o.value} className="text-sm">
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }
  return (
    <Input className="h-8 text-sm" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
  )
}

function FieldHistory({ recordId, field, label }: { recordId: string; field: string; label: string }) {
  const [events, setEvents] = useState<FormRecordEvent[] | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (open && !events) loadFieldHistory(recordId, field).then(setEvents)
  }, [open, events, recordId, field])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="text-muted-foreground hover:text-foreground" title="Change history">
          <Clock className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="end">
        <div className="text-xs font-semibold mb-2">{label} — history</div>
        {!events && <p className="text-xs text-muted-foreground">Loading…</p>}
        {events && events.length === 0 && <p className="text-xs text-muted-foreground">No changes recorded.</p>}
        <div className="space-y-2 max-h-64 overflow-auto">
          {events?.map((e) => (
            <div key={e.id} className={cn("border-l-2 pl-2 pb-1", e.source === "human" ? "border-blue-500" : "border-violet-600")}>
              <div className="text-[10px] text-muted-foreground">{new Date(e.created_at).toLocaleString()}</div>
              <div className="text-xs">
                <span className={cn("font-medium", e.source === "human" ? "text-blue-600" : "text-violet-600")}>
                  {e.source === "human" ? "Human" : e.source === "agent" ? "Agent" : "Import"}
                </span>{" "}
                {stringify(e.old_value)} → <b>{stringify(e.new_value)}</b>
                {e.confidence != null && <span className="text-muted-foreground"> · conf {e.confidence.toFixed(2)}</span>}
              </div>
              {e.note && <div className="text-[10px] text-muted-foreground italic">note: {e.note}</div>}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function stringify(v: any) {
  if (v === null || v === undefined || v === "") return "empty"
  if (Array.isArray(v)) return v.length ? v.join(", ") : "empty"
  if (typeof v === "boolean") return v ? "true" : "false"
  return String(v)
}

function labelFor(field: FormFieldDef, v: any) {
  return field.list?.find((o) => o.value === v)?.label ?? String(v)
}

function formatValue(field: FormFieldDef, value: any) {
  if (isValueEmpty(value)) return "Click to fill…"
  if (field.type === "boolean") return value ? "true" : "false"
  if (field.type === "date") return formatDate(value)
  if (field.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [value]
    return arr.map((v) => labelFor(field, v)).join(", ")
  }
  if (field.type === "list" && field.list) {
    return labelFor(field, value)
  }
  return String(value)
}

function calcTooltip(field: FormFieldDef, values: Record<string, any>, result: any): string {
  const formula = field.calculated?.formula
  if (!formula) return ""
  const resolved = formula.replace(/\{([^}]+)\}/g, (_, name) => {
    const v = values[name.trim()]
    return isValueEmpty(v) ? "∅" : String(Array.isArray(v) ? `[${v.length}]` : v)
  })
  const out = isValueEmpty(result) ? "—" : String(result)
  return resolved === formula
    ? `${formula} = ${out}`
    : `${formula}\n= ${resolved}\n= ${out}`
}

function formatDate(value: any): string {
  if (isValueEmpty(value)) return ""
  const s = String(value)
  // Parse date-only as UTC so it doesn't shift a day due to the local timezone.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  const d = m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : new Date(s)
  if (Number.isNaN(d.getTime())) return s
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" })
}

function isValueEmpty(value: any) {
  if (value === null || value === undefined || value === "") return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}
